/**
 * Text classes.
 *
 * Sizes are measured off the live landing pages, not invented — see the teardown notes on
 * each class. Two deliberate departures from the live site, both corrections rather than
 * mismatches:
 *
 * 1. **A 991px tier on the big headings.** The site takes `.heading-86` from 64px/91px
 *    straight to 40px at 479px, so a 64px heading with a 91px line-height renders inside a
 *    ~748px column at tablet width. We add an intermediate step.
 * 2. **No element margins.** Every layout here is gap-driven, so a margin on the element adds
 *    to the container's gap rather than replacing it. Spacing is the container's job.
 *
 * Every class declares the full `REQUIRED_TEXT_PROPS` set explicitly, because Collctiv's
 * 391KB stylesheet sets `body { font-family: Roboto }` and `h1 { font-family: Inter }` and
 * those inherit straight into a pasted section.
 */

import type { Longhand } from '../longhand.js';
import { colour, space } from '../tokens.js';
import { font, TEXT_RESET, type StyleDef } from '../styleDef.js';

const text = (
  name: string,
  scale: Parameters<typeof font>[0],
  extra: Longhand = {},
  variants?: StyleDef['variants'],
): StyleDef => ({
  name,
  base: {
    ...TEXT_RESET,
    ...font(scale),
    color: colour.ink,
    'text-align': 'left',
    // A long word — a URL, a product name — would otherwise push the whole column wider
    // than its track rather than breaking.
    'overflow-wrap': 'break-word',
    ...extra,
  },
  ...(variants ? { variants } : {}),
});

/** Centred section heading. 64px/91px on the live site. */
export const lpDisplay = text('lp-display', 'display', { 'text-align': 'center' }, {
  medium: { 'font-size': '48px', 'line-height': '62px' },
  small: { 'font-size': '40px', 'line-height': '54px' },
});

export const lpH1 = text('lp-h1', 'h1', {}, {
  medium: { 'text-align': 'center' },
  small: { 'font-size': '44px', 'line-height': '58px' },
  tiny: { 'font-size': '40px', 'line-height': '54px' },
});

/** Feature-row heading. */
export const lpH2 = text('lp-h2', 'h2', {}, {
  medium: { 'font-size': '44px', 'line-height': '56px' },
  small: { 'font-size': '36px', 'line-height': '46px' },
  tiny: { 'font-size': '32px', 'line-height': '42px' },
});

/** Heading inside the frosted purple panel. */
export const lpH2Panel = text('lp-h2-panel', 'h2Panel', { color: colour.white }, {
  small: { 'font-size': '36px', 'line-height': '44px' },
});

export const lpH3 = text('lp-h3', 'h3');
export const lpH4 = text('lp-h4', 'h4');

/** Hero subhead. */
export const lpLead = text('lp-lead', 'lead', {}, {
  medium: { 'text-align': 'center' },
  small: { 'font-size': '20px', 'line-height': '30px' },
});

export const lpBody = text('lp-body', 'body', {}, {
  small: { 'font-size': '18px', 'line-height': '26px' },
});

/** Dense copy — FAQ answers, captions, fine print. */
export const lpBodySm = text('lp-body-sm', 'bodySm');

export const lpSmall = text('lp-small', 'small', { color: colour.inkMuted });

export const lpEyebrow = text('lp-eyebrow', 'eyebrow', {
  color: colour.inkMuted,
  'text-transform': 'uppercase',
}, {
  small: { 'font-size': '13px', 'line-height': '21px' },
});

/** Big pink number in the stats band. */
export const lpStat = text('lp-stat', 'stat', { color: colour.pink, 'text-align': 'center' }, {
  medium: { 'font-size': '48px', 'line-height': '62px' },
  small: { 'font-size': '40px', 'line-height': '52px' },
});

export const lpStatLabel = text('lp-stat-label', 'statLabel', {
  color: colour.white,
  'text-align': 'center',
}, {
  small: { 'font-size': '24px', 'line-height': '32px' },
});

/** Numbered step marker in the purple band. */
export const lpStepNumber = text('lp-step-number', 'h2Panel', {
  color: colour.white,
  width: '44px',
  'min-width': '44px',
});

/**
 * Inline link inside body copy.
 *
 * Underlined, never colour alone — the live site's CTAs have no focus styles at all and set
 * `outline: none`, which we are not copying.
 */
export const lpLink: StyleDef = {
  name: 'lp-link',
  base: {
    ...TEXT_RESET,
    'font-family': 'inherit',
    'font-size': 'inherit',
    'line-height': 'inherit',
    'font-weight': '700',
    color: colour.pink,
    'text-decoration-line': 'underline',
    'text-decoration-color': colour.pink,
    'transition-property': 'color',
    'transition-duration': '150ms',
    'transition-timing-function': 'ease-out',
  },
  variants: { hover: { color: colour.indigo, 'text-decoration-color': colour.indigo } },
};

/** Lists outside rich text. */
export const lpList: StyleDef = {
  name: 'lp-list',
  base: {
    ...TEXT_RESET,
    ...font('body'),
    color: colour.ink,
    'padding-left': space[5],
    'list-style-type': 'disc',
    'list-style-position': 'outside',
    'text-align': 'left',
  },
};

export const lpListItem: StyleDef = {
  name: 'lp-list-item',
  base: { ...TEXT_RESET, ...font('body'), color: colour.ink, 'text-align': 'left' },
};

/** Rich text body — styles its own children, so it only sets the inherited basics. */
export const lpRichText: StyleDef = {
  name: 'lp-rich-text',
  base: { ...TEXT_RESET, ...font('body'), color: colour.ink, 'text-align': 'left' },
};

const TEXT_BASES = [
  'lp-display',
  'lp-h1',
  'lp-h2',
  'lp-h2-panel',
  'lp-h3',
  'lp-h4',
  'lp-lead',
  'lp-body',
  'lp-body-sm',
  'lp-small',
  'lp-eyebrow',
  'lp-rich-text',
  'lp-list',
  'lp-list-item',
] as const;

/** Inverted text on the purple bands. */
export const onDarkCombos: StyleDef[] = TEXT_BASES.map((parent) => ({
  name: `is-on-dark-${parent.replace('lp-', '')}`,
  comb: true as const,
  parent,
  base: { color: colour.white } satisfies Longhand,
}));

/** Per-element alignment, since text-align on the container does not always reach. */
export const textAlignCombos: StyleDef[] = TEXT_BASES.flatMap((parent) =>
  (['left', 'center', 'right'] as const).map((align) => ({
    name: `is-${align}-${parent.replace('lp-', '')}`,
    comb: true as const,
    parent,
    base: { 'text-align': align } satisfies Longhand,
  })),
);

export const textStyles: StyleDef[] = [
  lpDisplay,
  lpH1,
  lpH2,
  lpH2Panel,
  lpH3,
  lpH4,
  lpLead,
  lpBody,
  lpBodySm,
  lpSmall,
  lpEyebrow,
  lpStat,
  lpStatLabel,
  lpStepNumber,
  lpLink,
  lpList,
  lpListItem,
  lpRichText,
  ...onDarkCombos,
  ...textAlignCombos,
];
